import { useEffect, useState, useRef } from "react";
import {
  EditableSelectComponent,
  SectionHeader,
  EditableTextareaComponent,
} from "components/index";
import { Bulb } from "assets/icons";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import { FileLabel } from "shared/types/file";
// Meters verhuisd naar voorzieningen (assets) — sectie verborgen.
// import ElectricityMeters from "./electricityMeters";
import {
  ElectricityConnectedTo,
  ElectricityInfoDto,
  ElectricityInfoLevel,
} from "shared/types/electricity";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { UnitDTO } from "shared/types/unit";
import { BuildingDTO } from "shared/types/building";
import FileLogTable from "shared/components/uploadFileModal/fileLogTable";

interface ElectricityInfoProps {
  electricityInfo: ElectricityInfoDto;
  handleElectricityInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  onEditClick?: () => any;
  parentEditMode?: boolean;
  unit: UnitDTO | undefined | null;
  building: BuildingDTO | undefined | null;
  // isLoading: boolean;
}

const ElectricityInfoComponent = ({
  electricityInfo,
  handleElectricityInfoChangeInParent,
  handleSaveInParent,
  handleCloseParent,
  onEditClick,
  parentEditMode = true,
  // isLoading = false,
  unit,
  building,
}: ElectricityInfoProps) => {
  const [editMode, setEditMode] = useState<boolean>(parentEditMode || true);
  const [editedElectricityInfo, setEditedElectricityInfo] =
    useState<ElectricityInfoDto | null>(null);
  const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
  const [selectedFileLabel, setSelectedFileLabel] = useState<FileLabel | null>(
    null,
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const fileLogTableRef = useRef<{ saveChanges: () => Promise<void>; hasUnsavedChanges: boolean } | null>(null);


  const { session } = useSession();

  const localOnEditClick = () => {
    setEditMode((prevMode) => !prevMode);
    if (!editMode) {
      setEditedElectricityInfo(electricityInfo);
    }
  };

  useEffect(() => {
    setEditMode(parentEditMode);
  }, [parentEditMode]);

  const electricityFileLabels = [
    FileLabel.ElektriciteitKeuringsattestUnit,
    FileLabel.ElektriciteitKeuringsattestGebouw,
    FileLabel.ElektriciteitEendraadsschemaUnit,
    FileLabel.ElektriciteitEendraadsschemaGebouw,
    FileLabel.ElektriciteitSituatieschetsUnit,
    FileLabel.ElektriciteitSituatieschetsBuilding,
  ];




  const getStatusForElectricityConnectedTo = (connectedTo: any) => {
    return connectedTo
      ? connectedTo == ElectricityConnectedTo.PRIVATE
        ? "bg-green-500"
        : "bg-orange-500"
      : "bg-transparent-500";
  };


  useEffect(() => {
    setEditedElectricityInfo(electricityInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch



  const handleLocalElectricitySave = async () => {
    if (!editedElectricityInfo) {
      toast.error("Er werd geen elektriciteitsdata gevonden");
      return;
    }

    try {
      await fetchWithAuth(
        `${API_URL}/energy/electricity-info`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editedElectricityInfo),
        },
        session,
      );

      toast.success(
        `Elektrische gegevens geupdate voor ${electricityInfo.level === ElectricityInfoLevel.UNIT
          ? electricityInfo.unitId
          : electricityInfo.buildingId
        }!`,
      );
    } catch (e: any) {
      console.log(e);
    }
  };

  const handleFileModalClose = async () => {
    setSelectedFileLabel(null);
    setFileModalOpen(false);
  };

  const updateElectricityInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedElectricityInfo((prev: any) => ({ ...prev, ...updatedFields }));

    if (handleElectricityInfoChangeInParent) {
      handleElectricityInfoChangeInParent({
        ...electricityInfo,
        ...updatedFields,
      });
    }
  };

  const onUpdateConnectedTo = (connectedTo: any) => {
    updateElectricityInfo({ electricityConnectedTo: connectedTo });
  };

  const onUpdateElectricityRemarks = (remarks: string) => {
    updateElectricityInfo({ remarks: remarks });
  };

  const handleSave = async () => {
    setHasUnsavedChanges(false);

    // First save the file log changes if there are any
    if (fileLogTableRef.current?.hasUnsavedChanges) {
      await fileLogTableRef.current.saveChanges();
    }

    // Then save the electricity info
    if (handleSaveInParent) {
      await handleSaveInParent();
    } else {
      await handleLocalElectricitySave();
    }
  };

  const handleFileUploadSuccess = async (fileLogData: any) => {
    const { fileLog } = fileLogData;

    let updatedField = {};

    switch (selectedFileLabel) {
      case FileLabel.ElektriciteitKeuringsattestUnit:
      case FileLabel.ElektriciteitKeuringsattestGebouw:
        updatedField = {
          electricityCertificateFileLogId: fileLog.id,
          electricityCertificateFileLog: fileLog,
        };
        break;
      case FileLabel.ElektriciteitEendraadsschemaUnit:
      case FileLabel.ElektriciteitEendraadsschemaGebouw:
        updatedField = {
          electricitySchemaFileLogId: fileLog.id,
          electricitySchemaFileLog: fileLog,
        };
        break;
      case FileLabel.ElektriciteitSituatieschetsUnit:
      case FileLabel.ElektriciteitSituatieschetsBuilding:
        updatedField = {
          electricitySituationPlanFileLogId: fileLog.id,
          electricitySituationPlanFileLog: fileLog,
        };
        break;
      // Add more cases as needed
      default:
        return;
    }

    const updatedElectricityInfo = { ...editedElectricityInfo as any, ...updatedField };

    // Save the updated information to the backend
    try {
      const response = await fetchWithAuth(
        `${API_URL}/energy/electricity-info`,
        {
          method: "POST", // or POST, depending on your API
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedElectricityInfo),
        },
        session,
      );

      if (response.ok) {
        toast.success("Electricity information updated successfully");
        setHasUnsavedChanges(false); // Reset unsaved changes state
        setEditedElectricityInfo(updatedElectricityInfo); // Update local state
      } else {
        throw new Error("Failed to update electricity information");
      }
    } catch (error) {
      console.error("Error updating electricity information:", error);
      toast.error("Failed to update electricity information");
    }
  };

  return (
    <section
      key="electricity"
      className="flex flex-col gap-0 rounded-md bg-white"
    >
      <SectionHeader
        title="Elektriciteit"
        icon={<Bulb />}
        editMode={editMode}
        sticky={false}
        onEditClick={onEditClick || localOnEditClick}
        onSaveClick={() => handleSave()}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges || fileLogTableRef.current?.hasUnsavedChanges}
        onCloseClick={() => {
          if (handleCloseParent) handleCloseParent();
        }}
        hideCloseAction={true}
      />

      <div className="">
        <div className="gap-3 sm:grid sm:grid-cols-2">
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusForElectricityConnectedTo(
                  editedElectricityInfo?.electricityConnectedTo,
                )}`}
              />
              <p>Elektriciteitsinstallatie aangesloten op</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze"
              value={
                editedElectricityInfo?.electricityConnectedTo
                  ? editedElectricityInfo?.electricityConnectedTo
                  : ""
              }
              options={Object.values(ElectricityConnectedTo).map(
                (value) => ({
                  label: value,
                  value,
                }),
              )}
              editMode={editMode}
              onChange={onUpdateConnectedTo}
            />
          </div>

          {/* remarks section */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Opmerkingen</p>
            </header>
            <EditableTextareaComponent
              label="Opmerkingen"
              value={editedElectricityInfo?.remarks || ""}
              editMode={editMode}
              onChange={onUpdateElectricityRemarks}
            />
          </div>
          

          <div className="col-span-2 flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Elektriciteitsdocumenten</p>
            </header>
            <FileLogTable
              ref={fileLogTableRef}
              acceptedLabels={electricityFileLabels}
              onUploadClick={() => setFileModalOpen(true)}
              building={building || undefined}
              unit={unit || undefined}
            />
          </div>


          {/*
            Meters worden nu beheerd als voorzieningen (assets), niet meer hier.
            Sectie verborgen — zie Instellingen > "Meters in documenten".
            {electricityInfo && electricityInfo.id && (
              <ElectricityMeters
                meters={electricityInfo.electricityMeter}
                electricityInfoId={electricityInfo && electricityInfo.id}
              />
            )}
          */}
        </div>
      </div>

      <ModalUploadFileLog
        isOpen={fileModalOpen}
        onClose={handleFileModalClose}
        unit={unit}
        building={building}
        customLabels={unit ? 
          [FileLabel.ElektriciteitKeuringsattestUnit, FileLabel.ElektriciteitEendraadsschemaUnit, FileLabel.ElektriciteitSituatieschetsUnit] : 
          [FileLabel.ElektriciteitKeuringsattestGebouw, FileLabel.ElektriciteitEendraadsschemaGebouw, FileLabel.ElektriciteitSituatieschetsBuilding]}
        onFileUploadSuccess={handleFileUploadSuccess}
      />
      {/* ModalUploadFileLog and any other additional UI elements */}
    </section>
  );
};

export default ElectricityInfoComponent;
